// Command — the personal "monitoring production of myself" widget.
// Three states: Triage (morning) → Live (daytime) → Green (you're done).
// Daily model = Floor + 1 Needle-Mover + green-to-stop. Measured in
// commitments cleared, not output produced.
// Design: docs/plans/2026-05-31-command-widget-design.md
import { useState, useMemo, useEffect } from "react";
import {
  Sparkles,
  Check,
  X,
  Loader2,
  Clock,
  AlertTriangle,
  MessageSquare,
  ArrowRight,
  Target,
  Shield,
  PartyPopper,
  Copy,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import {
  useCommandToday,
  useProposeFloor,
  useAcceptTriage,
  useToggleCommitment,
  useSetGreen,
  useSummarizeDay,
  computeWon,
  type Proposal,
  type FloorCandidate,
  type CommandCommitment,
  type JobRisk,
} from "@/hooks/useCommand";
import { useChat } from "@/hooks/useChat";

const RISK_LABEL: Record<JobRisk, string> = {
  high: "job risk: high",
  med: "job risk: med",
  low: "job risk: low",
};

function riskTone(risk: JobRisk | null): string {
  if (risk === "high") return "border-destructive/50 text-destructive";
  if (risk === "med") return "border-warning/50 text-warning";
  return "border-border text-muted-foreground";
}

function channelLabel(ch: string | null): string {
  if (!ch) return "";
  return ch.charAt(0).toUpperCase() + ch.slice(1);
}

export default function Command() {
  const { dayQuery, commitmentsQuery, day } = useCommandToday();
  const propose = useProposeFloor();
  const accept = useAcceptTriage();
  const toggle = useToggleCommitment();
  const setGreen = useSetGreen();
  const summarize = useSummarizeDay();

  const [proposal, setProposal] = useState<Proposal | null>(null);
  const [openCommitment, setOpenCommitment] = useState<CommandCommitment | null>(null);
  const [summary, setSummary] = useState<string | null>(null);

  const commitments = commitmentsQuery.data ?? [];
  const dayRow = dayQuery.data;
  const floor = commitments.filter((c) => c.kind === "floor");
  const needle = commitments.filter((c) => c.kind === "needle");
  const parked = commitments.filter((c) => c.kind === "parked");
  const won = useMemo(() => computeWon(commitments), [commitments]);
  const capHours = dayRow?.cap_hours ?? 9;
  const plannedHours = useMemo(
    () =>
      [...floor, ...needle].reduce(
        (s, c) => s + (Number(c.effort_hours) || 0),
        0
      ),
    [floor, needle]
  );
  const overCap = plannedHours > capHours;

  // Keep the green stamp in sync with the won state.
  useEffect(() => {
    if (commitmentsQuery.isLoading || floor.length === 0) return;
    const stamped = !!dayRow?.green_at;
    if (won && !stamped) setGreen.mutate(true);
    if (!won && stamped) setGreen.mutate(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [won, dayRow?.green_at, floor.length, commitmentsQuery.isLoading]);

  const handlePropose = async () => {
    try {
      const result = await propose.mutateAsync();
      setProposal(result);
      if ((result.floor?.length ?? 0) === 0) {
        toast.message("Nothing waiting on you in the last 5 days. Clear runway.");
      }
    } catch (e) {
      toast.error(`Couldn't read your channels: ${String(e)}`);
    }
  };

  const handleGenerateSummary = async () => {
    try {
      const result = await summarize.mutateAsync();
      if (!result.body) {
        toast.message(result.message || "Nothing completed yet to summarize.");
        return;
      }
      setSummary(result.body);
    } catch (e) {
      toast.error(`Summary failed: ${String(e)}`);
    }
  };

  const isLoading = dayQuery.isLoading || commitmentsQuery.isLoading;
  const hasLockedDay = floor.length > 0;
  const showTriage = !hasLockedDay && proposal !== null;
  const showStart = !hasLockedDay && proposal === null;

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col">
      <header className="flex items-center justify-between px-4 py-3 border-b border-border">
        <div className="flex items-center gap-2">
          <Target className="size-4 text-primary" />
          <span className="text-sm font-semibold tracking-tight">Command</span>
          <span className="text-xs text-muted-foreground">{day}</span>
        </div>
        {hasLockedDay && (
          <button
            onClick={handlePropose}
            disabled={propose.isPending}
            className="text-xs text-muted-foreground hover:text-foreground transition-colors inline-flex items-center gap-1"
          >
            {propose.isPending ? (
              <Loader2 className="size-3 animate-spin" />
            ) : (
              <Sparkles className="size-3" />
            )}
            Re-triage
          </button>
        )}
      </header>

      <main className="flex-1 p-4 space-y-4 overflow-y-auto">
        {isLoading && (
          <div className="flex items-center justify-center py-16 text-muted-foreground">
            <Loader2 className="size-5 animate-spin" />
          </div>
        )}

        {/* START — propose the day */}
        {!isLoading && showStart && (
          <div className="flex flex-col items-center text-center py-12 gap-4">
            <Target className="size-10 text-primary" />
            <div>
              <h2 className="text-lg font-semibold">Plan today's Floor</h2>
              <p className="text-sm text-muted-foreground max-w-xs mt-1">
                I'll read the last 5 days across your channels and pull out the
                few things that actually protect the job — then you decide.
              </p>
            </div>
            <Button onClick={handlePropose} disabled={propose.isPending}>
              {propose.isPending ? (
                <>
                  <Loader2 className="size-4 animate-spin" /> Reading your channels…
                </>
              ) : (
                <>
                  <Sparkles className="size-4" /> Propose my Floor
                </>
              )}
            </Button>
          </div>
        )}

        {/* TRIAGE — review + lock in */}
        {!isLoading && showTriage && proposal && (
          <TriageView
            proposal={proposal}
            isSaving={accept.isPending}
            onLockIn={async (payload) => {
              try {
                await accept.mutateAsync(payload);
                setProposal(null);
                toast.success("Day locked in. Clear the Floor and log off.");
              } catch (e) {
                toast.error(`Couldn't lock in: ${String(e)}`);
              }
            }}
            onDiscard={() => setProposal(null)}
          />
        )}

        {/* GREEN — you're done */}
        {!isLoading && hasLockedDay && won && (
          <div className="rounded-lg border border-success/40 bg-success/10 p-5 text-center space-y-3">
            <PartyPopper className="size-9 text-success mx-auto" />
            <div>
              <h2 className="text-lg font-semibold text-success">You're done.</h2>
              <p className="text-sm text-muted-foreground">
                Floor cleared{needle.length > 0 ? " + needle-mover done" : ""}. Log
                off — you've earned it.
              </p>
            </div>
            <div className="flex flex-col gap-2 pt-1">
              <Button onClick={handleGenerateSummary} disabled={summarize.isPending}>
                {summarize.isPending ? (
                  <Loader2 className="size-4 animate-spin" />
                ) : (
                  <Shield className="size-4" />
                )}
                Generate Dalin summary
              </Button>
              <Button
                variant="outline"
                onClick={handlePropose}
                disabled={propose.isPending}
              >
                <ArrowRight className="size-4" /> Plan tomorrow
              </Button>
            </div>
          </div>
        )}

        {/* LIVE — work the Floor */}
        {!isLoading && hasLockedDay && (
          <div className="space-y-4">
            {!won && (
              <CapLine
                planned={plannedHours}
                cap={capHours}
                overCap={overCap}
                incoming={parked.length}
              />
            )}

            <section className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Floor
                </span>
                <span className="text-xs text-muted-foreground">
                  {floor.filter((c) => c.state === "done").length}/{floor.length}
                </span>
              </div>
              {floor.map((c) => (
                <CommitmentRow
                  key={c.id}
                  c={c}
                  onToggle={(done) => toggle.mutate({ id: c.id, done })}
                  onOpen={() => setOpenCommitment(c)}
                />
              ))}
            </section>

            {needle.length > 0 && (
              <section className="space-y-2">
                <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Needle-Mover
                </span>
                {needle.map((c) => (
                  <CommitmentRow
                    key={c.id}
                    c={c}
                    onToggle={(done) => toggle.mutate({ id: c.id, done })}
                    onOpen={() => setOpenCommitment(c)}
                  />
                ))}
              </section>
            )}

            {parked.length > 0 && (
              <section className="space-y-1.5 pt-1">
                <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Not today · {parked.length}
                </span>
                <div className="space-y-1">
                  {parked.map((c) => (
                    <div
                      key={c.id}
                      className="text-xs text-muted-foreground truncate px-2 py-1 rounded bg-muted/40"
                    >
                      {c.title}
                    </div>
                  ))}
                </div>
              </section>
            )}
          </div>
        )}
      </main>

      {/* Item-scoped AI assistant */}
      <Sheet
        open={!!openCommitment}
        onOpenChange={(o) => !o && setOpenCommitment(null)}
      >
        <SheetContent className="w-full sm:max-w-md flex flex-col">
          {openCommitment && (
            <CommitmentDetail
              c={openCommitment}
              onToggle={(done) => {
                toggle.mutate({ id: openCommitment.id, done });
                setOpenCommitment(null);
              }}
            />
          )}
        </SheetContent>
      </Sheet>

      {/* Dalin summary */}
      <Sheet open={!!summary} onOpenChange={(o) => !o && setSummary(null)}>
        <SheetContent className="w-full sm:max-w-md flex flex-col">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">
              <Shield className="size-4" /> Summary for Dalin
            </SheetTitle>
            <SheetDescription>
              What you shipped today, in plain English. Copy and send.
            </SheetDescription>
          </SheetHeader>
          <div className="flex-1 overflow-y-auto mt-3 text-sm whitespace-pre-wrap leading-relaxed">
            {summary}
          </div>
          <Button
            className="mt-3"
            onClick={() => {
              if (summary) {
                navigator.clipboard.writeText(summary);
                toast.success("Copied.");
              }
            }}
          >
            <Copy className="size-4" /> Copy
          </Button>
        </SheetContent>
      </Sheet>
    </div>
  );
}

// ── Cap line ────────────────────────────────────────────────────────────────

function CapLine({
  planned,
  cap,
  overCap,
  incoming,
}: {
  planned: number;
  cap: number;
  overCap: boolean;
  incoming: number;
}) {
  return (
    <div
      className={`flex items-center justify-between rounded-md border px-3 py-2 text-xs ${
        overCap
          ? "border-destructive/50 bg-destructive/10 text-destructive"
          : "border-border bg-card text-muted-foreground"
      }`}
    >
      <span className="inline-flex items-center gap-1.5">
        {overCap ? (
          <AlertTriangle className="size-3.5" />
        ) : (
          <Clock className="size-3.5" />
        )}
        {overCap
          ? `Today's Floor ≈ ${planned}h — over your ${cap}h cap. Push something.`
          : `Planned ≈ ${planned}h of ${cap}h`}
      </span>
      {incoming > 0 && <span>{incoming} parked</span>}
    </div>
  );
}

// ── Commitment row ───────────────────────────────────────────────────────────

function CommitmentRow({
  c,
  onToggle,
  onOpen,
}: {
  c: CommandCommitment;
  onToggle: (done: boolean) => void;
  onOpen: () => void;
}) {
  const done = c.state === "done";
  return (
    <div
      className={`group flex items-start gap-2.5 rounded-md border border-border bg-card p-2.5 transition-colors ${
        done ? "opacity-55" : ""
      }`}
    >
      <button
        onClick={() => onToggle(!done)}
        className={`mt-0.5 size-4 shrink-0 rounded-full border flex items-center justify-center transition-colors ${
          done
            ? "bg-success border-success text-success-foreground"
            : "border-muted-foreground/50 hover:border-foreground"
        }`}
        aria-label={done ? "Mark open" : "Mark done"}
      >
        {done && <Check className="size-3" />}
      </button>
      <button onClick={onOpen} className="flex-1 min-w-0 text-left">
        <div className={`text-sm leading-snug ${done ? "line-through" : ""}`}>
          {c.title}
        </div>
        <div className="flex flex-wrap items-center gap-1.5 mt-1">
          {c.waiting_on && (
            <span className="text-xs text-muted-foreground">→ {c.waiting_on}</span>
          )}
          {c.source_channel && (
            <Badge variant="outline" className="text-[10px] py-0 px-1.5 h-4">
              {channelLabel(c.source_channel)}
            </Badge>
          )}
          {c.kind === "floor" && c.job_risk && (
            <Badge
              variant="outline"
              className={`text-[10px] py-0 px-1.5 h-4 ${riskTone(c.job_risk)}`}
            >
              {RISK_LABEL[c.job_risk]}
            </Badge>
          )}
          {typeof c.effort_hours === "number" && c.effort_hours > 0 && (
            <span className="text-[10px] text-muted-foreground">
              ~{c.effort_hours}h
            </span>
          )}
        </div>
      </button>
      <MessageSquare
        className="size-3.5 mt-1 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity shrink-0 cursor-pointer"
        onClick={onOpen}
      />
    </div>
  );
}

// ── Triage view ──────────────────────────────────────────────────────────────

function TriageView({
  proposal,
  isSaving,
  onLockIn,
  onDiscard,
}: {
  proposal: Proposal;
  isSaving: boolean;
  onLockIn: (payload: {
    floor: FloorCandidate[];
    needle: FloorCandidate | null;
    parked: { title: string; item_ids: string[] }[];
  }) => void;
  onDiscard: () => void;
}) {
  // Track which floor candidates are kept (default all in).
  const [keptFloor, setKeptFloor] = useState<boolean[]>(
    proposal.floor.map(() => true)
  );
  const [needleIdx, setNeedleIdx] = useState<number>(
    proposal.needle_candidates.length > 0 ? 0 : -1
  );

  const keptCount = keptFloor.filter(Boolean).length;

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-sm font-semibold">Today's proposed Floor</h2>
        <p className="text-xs text-muted-foreground">
          Cut anything that isn't really job-critical. Pick one needle-mover.
        </p>
      </div>

      {proposal.floor.length === 0 && (
        <p className="text-sm text-muted-foreground py-4">
          Nothing's waiting on you. Pick a needle-mover below and go.
        </p>
      )}

      <div className="space-y-2">
        {proposal.floor.map((f, i) => (
          <div
            key={i}
            className={`flex items-start gap-2.5 rounded-md border p-2.5 transition-colors ${
              keptFloor[i] ? "border-border bg-card" : "border-border/50 opacity-50"
            }`}
          >
            <div className="flex-1 min-w-0">
              <div className="text-sm leading-snug">{f.title}</div>
              <div className="flex flex-wrap items-center gap-1.5 mt-1">
                {f.waiting_on && (
                  <span className="text-xs text-muted-foreground">
                    → {f.waiting_on}
                  </span>
                )}
                {f.source_channel && (
                  <Badge variant="outline" className="text-[10px] py-0 px-1.5 h-4">
                    {channelLabel(f.source_channel)}
                  </Badge>
                )}
                <Badge
                  variant="outline"
                  className={`text-[10px] py-0 px-1.5 h-4 ${riskTone(f.job_risk)}`}
                >
                  {RISK_LABEL[f.job_risk]}
                </Badge>
                {f.effort_hours > 0 && (
                  <span className="text-[10px] text-muted-foreground">
                    ~{f.effort_hours}h
                  </span>
                )}
              </div>
              {f.reason && (
                <p className="text-[11px] text-muted-foreground/80 mt-1 italic">
                  {f.reason}
                </p>
              )}
            </div>
            <button
              onClick={() =>
                setKeptFloor((prev) =>
                  prev.map((v, j) => (j === i ? !v : v))
                )
              }
              className="mt-0.5 size-5 shrink-0 rounded border border-border flex items-center justify-center hover:bg-muted transition-colors"
              aria-label={keptFloor[i] ? "Cut" : "Keep"}
            >
              {keptFloor[i] ? (
                <X className="size-3 text-muted-foreground" />
              ) : (
                <Check className="size-3 text-muted-foreground" />
              )}
            </button>
          </div>
        ))}
      </div>

      {proposal.needle_candidates.length > 0 && (
        <>
          <Separator />
          <div className="space-y-2">
            <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Pick one needle-mover
            </span>
            {proposal.needle_candidates.map((n, i) => (
              <button
                key={i}
                onClick={() => setNeedleIdx(needleIdx === i ? -1 : i)}
                className={`w-full text-left flex items-start gap-2.5 rounded-md border p-2.5 transition-colors ${
                  needleIdx === i
                    ? "border-primary bg-primary/10"
                    : "border-border bg-card hover:border-muted-foreground/40"
                }`}
              >
                <div
                  className={`mt-0.5 size-4 shrink-0 rounded-full border flex items-center justify-center ${
                    needleIdx === i
                      ? "bg-primary border-primary text-primary-foreground"
                      : "border-muted-foreground/50"
                  }`}
                >
                  {needleIdx === i && <Check className="size-3" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm leading-snug">{n.title}</div>
                  {n.reason && (
                    <p className="text-[11px] text-muted-foreground/80 mt-0.5 italic">
                      {n.reason}
                    </p>
                  )}
                </div>
              </button>
            ))}
          </div>
        </>
      )}

      <div className="flex gap-2 pt-2">
        <Button
          className="flex-1"
          disabled={isSaving || (keptCount === 0 && needleIdx === -1)}
          onClick={() =>
            onLockIn({
              floor: proposal.floor.filter((_, i) => keptFloor[i]),
              needle: needleIdx >= 0 ? proposal.needle_candidates[needleIdx] : null,
              parked: proposal.parked,
            })
          }
        >
          {isSaving ? (
            <Loader2 className="size-4 animate-spin" />
          ) : (
            <Check className="size-4" />
          )}
          Lock in the day
        </Button>
        <Button variant="outline" onClick={onDiscard} disabled={isSaving}>
          Discard
        </Button>
      </div>
    </div>
  );
}

// ── Commitment detail + scoped AI chat ───────────────────────────────────────

function CommitmentDetail({
  c,
  onToggle,
}: {
  c: CommandCommitment;
  onToggle: (done: boolean) => void;
}) {
  const { messages, sendMessage, isLoading } = useChat();
  const [input, setInput] = useState("");
  const done = c.state === "done";

  const contextPreamble = `I'm working on this commitment: "${c.title}"${
    c.waiting_on ? ` (waiting on: ${c.waiting_on})` : ""
  }${c.source_channel ? ` from ${c.source_channel}` : ""}${
    c.source_ref ? ` [source ref: ${c.source_ref}]` : ""
  }. `;

  const handleSend = () => {
    if (!input.trim()) return;
    const isFirst = messages.length === 0;
    sendMessage(isFirst ? contextPreamble + input.trim() : input.trim());
    setInput("");
  };

  return (
    <>
      <SheetHeader>
        <SheetTitle className="text-base leading-snug">{c.title}</SheetTitle>
        <SheetDescription>
          {c.waiting_on ? `Waiting on ${c.waiting_on}` : "No one specific waiting"}
          {c.source_channel ? ` · ${channelLabel(c.source_channel)}` : ""}
        </SheetDescription>
      </SheetHeader>

      <div className="mt-2">
        <Button
          variant={done ? "outline" : "default"}
          size="sm"
          onClick={() => onToggle(!done)}
        >
          <Check className="size-4" /> {done ? "Mark open" : "Mark done"}
        </Button>
      </div>

      <Separator className="my-3" />

      <div className="flex-1 overflow-y-auto space-y-3 text-sm">
        {messages.length === 0 && (
          <p className="text-xs text-muted-foreground">
            Ask the AI anything about this — it can pull the source thread, draft a
            reply, or kick off the work.
          </p>
        )}
        {messages.map((m, i) => (
          <div
            key={i}
            className={`rounded-md p-2.5 ${
              m.role === "user"
                ? "bg-primary/10 ml-6"
                : "bg-muted mr-6"
            }`}
          >
            <p className="whitespace-pre-wrap leading-relaxed">{m.content}</p>
          </div>
        ))}
        {isLoading && (
          <div className="flex items-center gap-2 text-muted-foreground text-xs">
            <Loader2 className="size-3 animate-spin" /> Thinking…
          </div>
        )}
      </div>

      <div className="mt-3 flex gap-2">
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              handleSend();
            }
          }}
          placeholder="Discuss this task…"
          className="flex-1 rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
        />
        <Button size="sm" onClick={handleSend} disabled={isLoading || !input.trim()}>
          <ArrowRight className="size-4" />
        </Button>
      </div>
    </>
  );
}
